import { ErrorBoundary } from 'react-error-boundary';
import App, { LoadingDelayedMessage } from './App';
import { useEffect, useState } from 'react';
import logger from './logger';
import { decryptData } from './utils';
import usePartySocket from 'partysocket/react';
import getBrowserFingerprint from 'get-browser-fingerprint';
import { HASURA_NAMES, PARTYKIT_HOST } from './constants';
import { Button, LogoutButton } from './Buttons';
import {
  configStore,
  logoutFn,
  pageStore,
  useDecodedToken,
  useToken,
  useUser,
} from './store';
import { useSetAtom } from 'jotai';
import { broadcastSessionEvent, subscribeToSessionBroadcast } from './session';
import type { SessionBroadcastMessage } from './session';

export const logError = (error: Error, info: React.ErrorInfo) => {
  logger.error(error.message, { error, errorInfo: info });
};

export function ErrorFallback() {
  const clearPages = useSetAtom(pageStore.clear);
  const clearConfig = useSetAtom(configStore.clear);

  return (
    <div className="flex flex-col items-center justify-center h-screen">
      <div className="flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold text-gray-900">
          Something went wrong
        </h1>
        <p className="text-gray-600">
          An error occurred while trying to render the application.
        </p>
        <button
          className="px-4 py-2 mt-4 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
          onClick={() => {
            clearPages();
            clearConfig();
            setTimeout(() => {
              window.location.reload();
            }, 500);
          }}
        >
          Try Again
        </button>
      </div>
    </div>
  );
}

async function generatePasskey(): Promise<string> {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  const arr = Array.from(array);
  const str = String.fromCharCode.apply(null, arr);
  return btoa(str);
}

let passkey = '';

export async function handleOpenFolio(code: string) {
  // Clear the logout flag when user clicks Connect to allow new authentication
  localStorage.removeItem('userLoggedOut');
  logger.info('User clicked Connect, cleared logout flag');

  localStorage.setItem('loginTime', String(Date.now()));
  const baseUrl = window.location.protocol + '//' + window.location.host;
  const prodUrl = 'https://folio.artis.works';
  const isProd = window.location.host === 'excel.artis.works';
  const folioUrl = (isProd ? prodUrl : baseUrl) + '/excel/login.html';
  const randomstring = await generatePasskey();
  const k = encodeURIComponent(randomstring);
  passkey = randomstring;
  const url = `${folioUrl}?passkey=${k}&pcode=${code}`;
  logger.debug('handleOpenFolio', { url });
  try {
    Office.context.ui.openBrowserWindow(url);
  } catch (error) {
    logger.debug('handleOpenFolio error, falling back to window.open', {
      error,
    });
    window.open(url, 'folioLogin');
  }
}

type Exchange = {
  code: number;
  source: number;
};

type AuthorizedPackage = {
  id: number;
  name: string;
  perm: string;
  sourceBySource: {
    exchange: Exchange;
  };
};

type HasuraClaims = {
  'authorized-packages': AuthorizedPackage[];
  'x-hasura-allowed-roles': string[];
  'x-hasura-default-role': string;
  'x-hasura-org-id': string;
  'x-hasura-user-email': string;
  'x-hasura-user-id': string;
};

export type JWT = {
  'https://hasura.io/jwt/claims': HasuraClaims;
  iss: string;
  sub: string;
  aud: string[];
  iat: number;
  exp: number;
  azp: string;
  scope: string;
  org_id: keyof typeof HASURA_NAMES;
};

export function userHasAddinRole(decoded: JWT) {
  logger.debug('checking user roles', { decoded });
  const roles =
    decoded['https://hasura.io/jwt/claims']['x-hasura-allowed-roles'];
  logger.debug('user roles', { roles });
  return roles.includes('excel-add-in');
}

export function checkToken({ decoded }: { decoded?: JWT }) {
  if (decoded === undefined) {
    return;
  }

  const msToExpiry = decoded.exp ? decoded.exp * 1000 - Date.now() : 0;
  const isExpired = msToExpiry <= 0;
  if (isExpired) {
    logger.info('Token has expired');
    logoutFn();
  }

  const userData = {
    sub: decoded.sub,
    email: decoded['https://hasura.io/jwt/claims']['x-hasura-user-email'],
  };

  const hasRole = userHasAddinRole(decoded);

  if (!hasRole) {
    logger.warn('User does not have addin role', { decoded });
    return {
      error:
        "You don't have access to the Artis for Excel service - please contact your Account Executive",
    };
  }

  console.log('token is valid', { userData, msToExpiry });

  return {
    user: userData,
    tokenMsToExpiry: msToExpiry,
  };
}

function usePartyToken({
  room,
  setError,
}: {
  room: string;
  setError: (e: string | null) => void;
}) {
  const [token, setToken] = useToken();
  const [errorCount, setErrorCount] = useState(0);
  const decoded = useDecodedToken();

  useEffect(() => {
    if (decoded?.error) {
      setError(decoded.error);
    } else if (decoded && !decoded.error) {
      // Clear error when token is valid
      setError(null);
    }
  }, [decoded, setError]);

  // Listen for token broadcasts from other add-ins on the same machine
  useEffect(() => {
    const handleBroadcast = (message: SessionBroadcastMessage) => {
      if (message.type === 'token_received') {
        // Accept token if we don't have one, or if current token has an error
        if (!token || decoded?.error) {
          logger.info('Received token broadcast from another add-in');
          setToken(message.token);
          setError(null);
        }
      }

      // If another add-in started a session and we have an error, clear it
      if (message.type === 'session_started' && decoded && !decoded.error) {
        setError(null);
      }
    };

    const unsubscribe = subscribeToSessionBroadcast(handleBroadcast);
    return unsubscribe;
  }, [token, decoded, setToken, setError]);

  const visibleErrorMessage = 'Error Logging In';
  usePartySocket({
    host: PARTYKIT_HOST,
    room,
    onOpen: () => {
      console.log('connected');
      setError(null);
      setErrorCount(0);
    },
    onMessage: async (e) => {
      console.log('message', e);
      if (e.data === 'ack') {
        return;
      }
      if (e.data === 'logout') {
        logoutFn();
        return;
      }

      // Check if user just logged out - if so, reject cached tokens from PartyKit
      const userLoggedOut = localStorage.getItem('userLoggedOut');
      if (userLoggedOut) {
        logger.info('User logged out, rejecting cached token from PartyKit', {
          logoutTimestamp: userLoggedOut,
        });
        // Don't accept cached token - user needs to authenticate again
        return;
      }

      decryptData(e.data, passkey)
        .then(async (decrypted) => {
          const decryptedData = JSON.parse(decrypted) as { jwt: string };
          setToken(decryptedData.jwt);
          setError(null);

          // Broadcast token to other add-ins on the same machine
          broadcastSessionEvent({
            type: 'token_received',
            token: decryptedData.jwt,
          });
          logger.info('Token received and broadcasted to other add-ins');
        })
        .catch((error) => {
          logger.debug('error decrypting token', { error });
          if (!decoded) {
            setError(visibleErrorMessage);
          }
        });
    },
    onClose: () => {
      console.log('disconnected');
    },
    onError: (e) => {
      console.log('error', e);
      setErrorCount((c) => c + 1);
      if (errorCount >= 5) {
        logoutFn();
      }
      if (!decoded) {
        setError(visibleErrorMessage);
      }
    },
  });

  return {
    token,
  };
}

export function AuthWrapper({ code }: { code: string }) {
  const [loading, setLoading] = useState(false);
  return (
    <div className="flex flex-col items-center justify-center h-screen">
      <div className="flex flex-col items-center justify-center">
        <p className="text-gray-600">Please connect to Artis to continue</p>
        <Button
          disabled={loading}
          onClick={() => {
            setLoading(true);
            handleOpenFolio(code);
            setTimeout(() => {
              window.focus();
            }, 1000);
          }}
        >
          {loading ? 'Authenticating...' : 'Connect'}
        </Button>
        {loading && <LoadingDelayedMessage />}
      </div>
    </div>
  );
}

export function useAuth() {
  const { expiresInMs, id, email, rawToken, error } = useUser();

  useEffect(() => {
    if (error) return;
    const timerId = setTimeout(() => {
      logger.info('Auth token expired. Logging out');
      logoutFn();
    }, expiresInMs);

    return () => {
      console.log('clearing the timeout', { expiresInMs });
      clearTimeout(timerId);
    };
  }, [expiresInMs, error]);

  return {
    user: {
      sub: id,
      email,
    },
    error,
    token: rawToken,
  };
}

export function AuthApp() {
  const code = String(getBrowserFingerprint());
  const [error, setError] = useState<string | null>(null);
  const { token } = usePartyToken({ room: code, setError });

  // Check if userLoggedOut flag is set
  const userLoggedOut = localStorage.getItem('userLoggedOut');
  const backupToken = localStorage.getItem('auth_token_backup');

  // Force reload if we have a backup token but no token in state
  // This handles the race condition with jotai-minidb hydration
  useEffect(() => {
    if (!token && backupToken && !userLoggedOut) {
      // Small delay to avoid reload loop
      const reloadKey = 'auth_reload_attempt';
      const lastReload = localStorage.getItem(reloadKey);
      const now = Date.now();
      if (!lastReload || now - parseInt(lastReload) > 5000) {
        localStorage.setItem(reloadKey, String(now));
        window.location.reload();
      }
    }
  }, [token, backupToken, userLoggedOut]);

  // If we have a valid token, show the app (ignore any lingering errors)
  // Only show error if we don't have a token
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onError={logError}>
      {token ? (
        <App />
      ) : error ? (
        <div className="relative flex flex-col items-center justify-center h-screen">
          <div className="flex flex-col items-center justify-center">
            <span className="text-sm m-4 text-center text-red-600">
              {error}
            </span>
            <div className="absolute bottom-0 left-0 m-4">
              <LogoutButton />
            </div>
          </div>
        </div>
      ) : (
        <AuthWrapper code={code} />
      )}
    </ErrorBoundary>
  );
}
